import React, { useState, useEffect } from 'react';
import { useApi } from '../../hooks/useApi';
import { generateCodingTask, generateMCQ, validateGeneratedTask, getGeneratedTasks } from '../../services/api';
import { formatContent, formatInstructions, formatExpectedOutput } from '../../utils/contentFormatter';
import CodeEditor from '../CodeEditor/CodeEditor';
import McqScreen from './McqScreen';
import './ContentScreen.css';

const ContentScreen = ({ screen, lessonId, onAnswer, subjectArea }) => {
  const { handleRequest } = useApi();
  const [generatedTasks, setGeneratedTasks] = useState({});
  const [showCodingTask, setShowCodingTask] = useState(false);
  const [showMCQ, setShowMCQ] = useState(false);
  const [loading, setLoading] = useState({ coding: false, mcq: false });
  const [code, setCode] = useState('');
  const [validationResult, setValidationResult] = useState(null);
  
  // Get examples from screen object (array format)
  const examples = screen.examples || [];

  console.log(screen);

  useEffect(() => {
    // Reset state when screen changes
    setGeneratedTasks({});
    setShowCodingTask(false);
    setShowMCQ(false);
    setValidationResult(null);
    
    // Load saved code from localStorage
    if (lessonId && screen.id) {
      const savedCode = localStorage.getItem(`lesson-code-${lessonId}-${screen.id}`);
      if (savedCode) {
        setCode(savedCode);
      }
    }
    
    // Load existing generated tasks for this screen
    if (lessonId && screen.id) {
      handleRequest(() => getGeneratedTasks(lessonId, screen.id))
        .then(response => {
          const tasks = response.data.tasks || [];
          const tasksMap = {};
          tasks.forEach(t => {
            tasksMap[t.type] = t.task;
          });
          setGeneratedTasks(tasksMap);
          if (tasksMap.coding) setShowCodingTask(true);
          if (tasksMap.mcq) setShowMCQ(true);
        })
        .catch(err => console.error('Kunne ikke laste genererte oppgaver:', err));
    }
  }, [lessonId, screen.id, handleRequest]);

  // Auto-save code to localStorage as user types (debounced)
  useEffect(() => {
    if (lessonId && screen.id && code !== undefined) {
      const timeoutId = setTimeout(() => {
        localStorage.setItem(`lesson-code-${lessonId}-${screen.id}`, code);
      }, 500); // Debounce: save 500ms after user stops typing

      return () => clearTimeout(timeoutId);
    }
  }, [code, lessonId, screen.id]);

  const handleGenerateCodingTask = async () => {
    setLoading(prev => ({ ...prev, coding: true }));
    try {
      const response = await handleRequest(generateCodingTask, lessonId, { screenId: screen.id });
      setGeneratedTasks(prev => ({ ...prev, coding: response.data.task }));
      setShowCodingTask(true);
    } catch (error) {
      console.error('Kunne ikke generere kodeoppgave:', error);
    } finally {
      setLoading(prev => ({ ...prev, coding: false }));
    }
  };

  const handleGenerateMCQ = async () => {
    setLoading(prev => ({ ...prev, mcq: true }));
    try {
      const response = await handleRequest(generateMCQ, lessonId, { screenId: screen.id });
      setGeneratedTasks(prev => ({ ...prev, mcq: response.data.question }));
      setShowMCQ(true);
    } catch (error) {
      console.error('Kunne ikke generere flervalgsspørsmål:', error);
    } finally {
      setLoading(prev => ({ ...prev, mcq: false }));
    }
  };

  const handleSubmitCode = async () => {
    if (!code.trim()) return;
    setLoading(prev => ({ ...prev, coding: true }));
    try {
      const response = await handleRequest(validateGeneratedTask, lessonId, {
        screenId: screen.id,
        taskType: 'coding',
        answer: code
      });
      setValidationResult(response.data);
      if (onAnswer) {
        onAnswer(code, response.data);
      }
    } catch (error) {
      console.error('Kunne ikke validere kode:', error);
    } finally {
      setLoading(prev => ({ ...prev, coding: false }));
    }
  };

  const handleMCQAnswer = async (answer, result) => {
    if (onAnswer) {
      onAnswer(answer, result);
    }
  };


  return (
    <div className="content-screen">
      <div className="content-panel">
        <div 
          className="content-text"
          dangerouslySetInnerHTML={{ __html: formatContent(screen.contentForTeacherOrSystem) }}
        />

        {/* Examples */}
        {examples.length > 0 && (
          <div className="examples-container">
            <div className="examples-header">
              <h3>Eksempler</h3>
            </div>
            {examples.map((example, index) => {
              // Use the type field from the example object
              const isCode = example.type === 'code';
              const isNote = example.type === 'note';
              
              return (
                <div 
                  key={index} 
                  className={`example-box ${
                    isCode ? 'example-code' : 
                    isNote ? 'example-note' : 
                    'example-text'
                  }`}
                >
                  {example.description && (
                    <div className="example-description">{example.description}</div>
                  )}
                  {isCode ? (
                    <div className="example-code-editor">
                      <CodeEditor
                        value={(example.content || '').replace(/\\n/g, '\n')}
                        onChange={() => {}} // No-op for read-only
                        language="javascript"
                        height="150px"
                        readOnly={true}
                      />
                    </div>
                  ) : (
                    <pre className="text-example">
                      {(example.content || '').replace(/\\n/g, '\n')}
                    </pre>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Generate Task Buttons */}
        {((subjectArea === 'programming' && !showCodingTask) || (!showMCQ && !generatedTasks.mcq)) && (
          <div className="generate-buttons">
            {subjectArea === 'programming' && !showCodingTask && (
              <button
                className="generate-button generate-coding"
                onClick={handleGenerateCodingTask}
                disabled={loading.coding}
              >
                {loading.coding ? 'Genererer...' : 'Generer kodeoppgave'}
              </button>
            )}
            {!showMCQ && !generatedTasks.mcq && (
              <button
                className="generate-button generate-mcq"
                onClick={handleGenerateMCQ}
                disabled={loading.mcq}
              >
                {loading.mcq ? 'Genererer...' : 'Generer flervalgsspørsmål'}
              </button>
            )}
          </div>
        )}

        {/* Generated Coding Task */}
        {subjectArea === 'programming' && showCodingTask && generatedTasks.coding && (
          <div className="generated-task coding-task">
            <div className="generated-task-left">
              <h3>Kodeoppgave</h3>
              <p><strong>Beskrivelse:</strong> {generatedTasks.coding.description}</p>
              <p><strong>Instruksjoner:</strong> <span dangerouslySetInnerHTML={{ __html: formatInstructions(generatedTasks.coding.instructions) }} /></p>
            
            </div>
            <div className="generated-task-right">
            <CodeEditor
              value={code}
              onChange={setCode}
              language="javascript"
              height="500px"
            />
              {validationResult && (
                <div className={`feedback ${validationResult.isCorrect ? 'feedback-correct' : 'feedback-incorrect'}`}>
                  {validationResult.feedback}
                </div>
              )}
              <button
                className="submit-code-button"
                onClick={handleSubmitCode}
                disabled={loading.coding || !code.trim()}
              >
                {loading.coding ? 'Validerer...' : 'Kjør kode'}
              </button>
              {generatedTasks.coding.expectedOutput && (
                <div className="expected-output-generated">
                  <strong>Forventet resultat:</strong>
                  <p dangerouslySetInnerHTML={{ __html: formatExpectedOutput(generatedTasks.coding.expectedOutput) }} />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Generated MCQ */}
        {showMCQ && generatedTasks.mcq && (
          <div className="generated-task mcq-task">
            <McqScreen
              screen={generatedTasks.mcq}
              onAnswer={handleMCQAnswer}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ContentScreen;
